import {
  BufferGeometry,
  Color,
  Float32BufferAttribute,
  NormalBlending,
  Points,
  ShaderMaterial,
  Vector3,
  type Scene,
} from 'three';
import { PALETTE } from '@/config';
import { RNG } from '@/utils/rng';

/**
 * Drifting paper motes by day, fireflies by night.
 *
 * ~500 points live inside a ~240m box that is kept centred on the player: each
 * particle's static home position is wrapped, modulo the box, relative to the
 * player's location in the vertex shader, so the field is always *around* you no
 * matter how far you walk — no CPU respawning, no per-frame buffer uploads. Motion
 * is a couple of slow, layered sines keyed off a per-particle seed, so nothing ever
 * moves in lockstep.
 *
 * Rendering is a tiny shader: a soft round sprite carved out of gl_PointCoord (no
 * texture), size-attenuated by distance. By day the motes are pale warm-grey
 * paper dust at low opacity; by night a quarter of them become warm lantern-orange
 * fireflies that pulse gently and swell a little larger. Depth-tested so the world
 * occludes them, but they never write depth, so they blend cleanly with each other.
 */

const COUNT = 500;
const BOX = 240; // side length of the wrap volume (m)
const FIREFLY_FRACTION = 0.25;

export class Motes {
  private readonly points: Points;
  private readonly material: ShaderMaterial;

  constructor(scene: Scene) {
    // Deterministic layout: motes seed from a fixed RNG rather than Math.random,
    // keeping the atmosphere reproducible frame-to-frame and run-to-run.
    const rng = new RNG(0x30a7e);
    const positions = new Float32Array(COUNT * 3);
    const seeds = new Float32Array(COUNT);
    const fire = new Float32Array(COUNT);

    const half = BOX * 0.5;
    for (let i = 0; i < COUNT; i++) {
      positions[i * 3 + 0] = rng.range(-half, half);
      positions[i * 3 + 1] = rng.range(-half, half);
      positions[i * 3 + 2] = rng.range(-half, half);
      seeds[i] = rng.next();
      // A stable per-particle roll decides which become fireflies at night.
      fire[i] = rng.next() < FIREFLY_FRACTION ? 1 : 0;
    }

    const geo = new BufferGeometry();
    geo.setAttribute('position', new Float32BufferAttribute(positions, 3));
    geo.setAttribute('aSeed', new Float32BufferAttribute(seeds, 1));
    geo.setAttribute('aFire', new Float32BufferAttribute(fire, 1));

    this.material = new ShaderMaterial({
      transparent: true,
      depthTest: true,
      depthWrite: false,
      blending: NormalBlending,
      fog: false,
      uniforms: {
        uTime: { value: 0 },
        uNight: { value: 0 },
        uPlayer: { value: new Vector3() },
        uBox: { value: BOX },
        // World-unit point sizes, converted to pixels by distance attenuation.
        uDaySize: { value: 0.09 },
        uFireSize: { value: 0.16 },
        uSizeScale: { value: 900.0 },
        uPaper: { value: new Color(PALETTE.fog).multiplyScalar(0.92) },
        uLantern: { value: new Color(PALETTE.lantern) },
      },
      vertexShader: /* glsl */ `
        attribute float aSeed;
        attribute float aFire;
        uniform float uTime, uNight, uBox, uDaySize, uFireSize, uSizeScale;
        uniform vec3 uPlayer;
        varying float vFire;   // 1 for an active firefly, else 0
        varying float vAlpha;  // per-particle brightness (pulse)

        void main() {
          float ph = aSeed * 6.2831853; // unique phase

          // Slow, layered drift — a gentle updraft plus lateral sway.
          vec3 drift = vec3(
            sin(uTime * 0.13 + ph),
            sin(uTime * 0.09 + ph * 1.7) * 0.5 + uTime * 0.15,
            cos(uTime * 0.11 + ph * 0.6)
          ) * 1.6;

          // Wrap the (static home + drift) into a box centred on the player.
          // NB: "half" is a reserved word in GLSL — hence "hb".
          float hb = uBox * 0.5;
          vec3 rel = position + drift - uPlayer;
          rel = mod(rel + hb, uBox) - hb;
          vec3 world = uPlayer + rel;

          vec4 mv = modelViewMatrix * vec4(world, 1.0);

          // Only the rolled fireflies light up, and only at night.
          vFire = aFire * uNight;
          float size = mix(uDaySize, uFireSize, vFire);
          gl_PointSize = clamp(size * uSizeScale / -mv.z, 1.0, 64.0);

          // Fireflies pulse individually; day motes hold a steady faint value.
          float pulse = 0.55 + 0.45 * sin(uTime * 1.3 + ph * 2.0);
          vAlpha = mix(1.0, pulse, vFire);

          gl_Position = projectionMatrix * mv;
        }
      `,
      fragmentShader: /* glsl */ `
        precision highp float;
        uniform float uNight;
        uniform vec3 uPaper, uLantern;
        varying float vFire;
        varying float vAlpha;

        void main() {
          // Soft round sprite from the point's own coordinate system.
          float d = length(gl_PointCoord - vec2(0.5));
          float mask = smoothstep(0.5, 0.0, d);
          if (mask <= 0.0) discard;

          // Fireflies glow lantern-warm; everything else is pale paper dust.
          vec3 col = mix(uPaper, uLantern, vFire);

          // Day motes cap out faint (<=0.35); fireflies are allowed to read.
          float base = mix(0.35, 0.9, vFire);
          // Day motes fade as night falls; fireflies own the night.
          float dayFade = mix(1.0, 0.35, uNight) * (1.0 - vFire) + vFire;

          gl_FragColor = vec4(col, mask * base * vAlpha * dayFade);
        }
      `,
    });

    this.points = new Points(geo, this.material);
    this.points.frustumCulled = false;
    // In front of ground/mountains/sky but part of the same atmospheric layer.
    this.points.renderOrder = 10;
    scene.add(this.points);
  }

  update(dt: number, elapsed: number, playerPos: Vector3, night: number): void {
    void dt; // motion is driven by absolute time; dt kept for API symmetry.
    this.material.uniforms.uTime.value = elapsed;
    this.material.uniforms.uNight.value = night;
    (this.material.uniforms.uPlayer.value as Vector3).copy(playerPos);
  }

  dispose(): void {
    this.points.geometry.dispose();
    this.material.dispose();
    this.points.removeFromParent();
  }
}
